import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Grid, IconButton, Typography } from '@mui/material';
import EditIconButton from '../../common/EditIconButton';
import TextInputDialog from '../../common/TextInputDialog';
import { useTranslation } from 'react-i18next';
import LocationChip from '../../settings/components/LocationChip';
import ManufacturingLocationSelectionDialog from '../../manufacturingLocation/components/ManufacturingLocationSelectionDialog';
import { ManufacturingLocation } from '../../manufacturingLocation/types';
import { HomeLinkInfo, HomeLinkUpdate } from '../types';
import {
  createHomeLinkGroupOptions,
  getHomeLinkDetails,
  HOME_LINK_IMAGE_MAX_FILE_SIZE_BYTES,
  HOME_LINK_MAX_FILE_SIZE_BYTES,
  homeLinkOptionToStringKey,
} from '../utils';
import {
  useDeleteHomeLinkFileMutation,
  useDeleteHomeLinkImageMutation,
  useDeleteHomeLinkMutation,
  useHomeLinkGroupsQuery,
  useHomeLinkQuery,
  useUpdateHomeLinkFileMutation,
  useUpdateHomeLinkImageMutation,
  useUpdateHomeLinkMutation,
} from '../queryHooks';
import { useIsMutating } from '@tanstack/react-query';
import { QueryKeys } from '../../../api/queryKeys';
import { DeleteIcon } from '../../common/icons';
import FileUploadDialog from '../../common/FileUploadDialog';
import EnhancedButton from '../../common/EnhancedButton';
import ConfirmDialog from '../../common/ConfirmDialog';
import Page from '../../common/Page';
import { useParams } from 'react-router-dom';
import { useNavigation } from '../../../utils/navigation';
import ImageCard from '../../common/ImageCard';
import EnhancedSelectDialog from '../../common/EnhancedSelectDialog';

/**
 * Component for editing Home Link details
 * @constructor
 */
const HomeLinkConfigDetailEdit = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { navigateToHomeConfiguration } = useNavigation();
  const { data: homeLink, isLoading } = useHomeLinkQuery(id);
  const { mutate: updateHomeLink } = useUpdateHomeLinkMutation();
  const { mutate: deleteHomeLink } = useDeleteHomeLinkMutation();
  const { mutate: updateImage } = useUpdateHomeLinkImageMutation();
  const { mutate: deleteImage } = useDeleteHomeLinkImageMutation();
  const { mutate: updateFile } = useUpdateHomeLinkFileMutation();
  const { mutate: deleteFile } = useDeleteHomeLinkFileMutation();
  const loadingHomeLinks =
    useIsMutating({ mutationKey: [QueryKeys.HOME_LINKS] }) > 0 || isLoading;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isIconImageAdd, setIsIconImageAdd] = useState(false);
  const [isFileAdd, setIsFileAdd] = useState(false);
  const [dialogOpenState, setDialogOpenState] = useState<DialogState>({});
  const [isDeleteIconImageOpen, setIsDeleteIconImageOpen] = useState(false);
  const [isDeleteFileOpen, setIsDeleteFileOpen] = useState(false);
  const [
    isManufacturingLocationDialogOpen,
    setIsManufacturingLocationDialogOpen,
  ] = useState(false);

  const inputProps = {
    min: 0,
    step: 1,
  };

  const {
    inventoryCenterId,
    mCellId,
    mCellCollectionId,
    title,
    sortSequence,
    homeLinkGroupId,
    groupName,
  } = getHomeLinkDetails(homeLink);

  const { data: homeLinkGroups = [] } = useHomeLinkGroupsQuery(
    { inventoryCenterId, mCellId, mCellCollectionId },
    !!homeLink,
  );
  const homeLinkGroupOptions = createHomeLinkGroupOptions(homeLinkGroups, t);

  /**
   * Opens the dialog window for a Home Link property
   * @param key
   */
  function handleDialogOpen(key: keyof HomeLinkInfo) {
    setDialogOpenState({
      ...dialogOpenState,
      [key]: true,
    });
  }

  /**
   * Closes the dialog window for a Home Link property
   * @param key
   */
  function handleDialogClose(key: keyof HomeLinkInfo) {
    setDialogOpenState({
      ...dialogOpenState,
      [key]: false,
    });
  }

  /**
   * Opens Delete Home Link dialog
   */
  function handleDeleteHomeLinkClick() {
    setIsDeleteDialogOpen(true);
  }

  /**
   * Deletes the Home Link and navigates to the Home Link Configuration page on success
   */
  function handleDeleteHomeLinkConfirm() {
    if (id) {
      deleteHomeLink(id, { onSuccess: navigateToHomeConfiguration });
    }
  }

  /**
   * Closes Delete Home Link dialog
   */
  function handleDeleteDialogClose() {
    setIsDeleteDialogOpen(false);
  }

  /**
   * Updates a Home Link property on the server and closes its dialog on success
   * @param key
   * @param newValue
   */
  function handleConfirm(key: keyof HomeLinkUpdate, newValue: any) {
    if (id && homeLink) {
      updateHomeLink(
        {
          id,
          info: {
            inventoryCenterId: homeLink.inventoryCenterId,
            mCellId: homeLink.mCellId,
            mCellCollectionId: homeLink.mCellCollectionId,
            title: homeLink.title,
            linkUrl: homeLink.linkUrl,
            sortSequence: homeLink.sortSequence,
            homeLinkGroupId: homeLink.homeLinkGroupId,
            [key]: newValue,
          },
        },
        { onSuccess: () => handleDialogClose(key) },
      );
    }
  }

  /**
   * Opens delete Icon Image dialog
   */
  function handleIconImageDeleteClick() {
    setIsDeleteIconImageOpen(true);
  }

  /**
   * Deletes the Icon Image and closes the dialog on success
   */
  function handleIconImageDeleteConfirm() {
    if (id && homeLink) {
      deleteImage(id, { onSuccess: () => setIsDeleteIconImageOpen(false) });
    }
  }

  /**
   * Closes delete Icon Image dialog
   */
  function handleIconImageDeleteClose() {
    setIsDeleteIconImageOpen(false);
  }

  /**
   * Deletes the File and closes the dialog on success
   */
  function handleFileDeleteConfirm() {
    if (id && homeLink) {
      deleteFile(id, { onSuccess: handleFileDeleteClose });
    }
  }

  /**
   * Closes delete File dialog
   */
  function handleFileDeleteClose() {
    setIsDeleteFileOpen(false);
  }

  /**
   * Called when location chip is clicked
   */
  function handleLocationClick() {
    setIsManufacturingLocationDialogOpen(true);
  }

  /**
   * Opens the add Icon Image dialog
   */
  function handleIconImageAddClick() {
    setIsIconImageAdd(true);
  }

  /**
   * Opens the add File dialog
   */
  function handleFileAddClick() {
    setIsFileAdd(true);
  }

  /**
   * Adds or replaces the Image and closes the dialog on success
   * @param image
   */
  function handleIconImageAddConfirm(image: File[]) {
    if (id && homeLink) {
      updateImage({ id, image }, { onSuccess: () => setIsIconImageAdd(false) });
    }
  }

  /**
   * Adds or replaces the File and closes the dialog on success
   * @param file
   */
  function handleFileAddConfirm(file: File[]) {
    if (id && homeLink) {
      updateFile({ id, file }, { onSuccess: () => setIsFileAdd(false) });
    }
  }

  /**
   * Closes add Icon Image dialog
   */
  function handleIconImageAddClose() {
    setIsIconImageAdd(false);
  }

  /**
   * Closes add File dialog
   */
  function handleFileAddClose() {
    setIsFileAdd(false);
  }

  /**
   * Called when manufacturing location dialog cancel is clicked.
   * Closes the dialog
   */
  function handleLocationDialogCancel() {
    setIsManufacturingLocationDialogOpen(false);
  }

  /**
   * Updates the Home Link's Manufacturing Location and closes the Manufacturing Location selection window.
   * @param manufacturingLocation
   */
  function handleConfirmManufacturingLocation(
    manufacturingLocation: ManufacturingLocation,
  ) {
    if (id && homeLink) {
      updateHomeLink({
        id,
        info: {
          inventoryCenterId: manufacturingLocation.inventoryCenterId,
          mCellId: manufacturingLocation.mCellId,
          mCellCollectionId: manufacturingLocation.mCellCollectionId,
          title: homeLink.title,
          linkUrl: homeLink.linkUrl,
          sortSequence: homeLink.sortSequence,
          homeLinkGroupId: homeLink.homeLinkGroupId,
        },
      });
    }
    setIsManufacturingLocationDialogOpen(false);
  }
  if (homeLink === undefined) {
    return isLoading ? null : <div>{t('homeLinkNotFound')}</div>;
  }

  return (
    <Page
      title={t('homeLinkDetails')}
      loading={loadingHomeLinks}
      actionComponent={
        <EnhancedButton onClick={handleDeleteHomeLinkClick}>
          {t('deleteHomeLink')}
        </EnhancedButton>
      }
    >
      <Grid sx={{ px: 3, pb: 3 }} container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }} container>
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant={'caption'} color={'textSecondary'}>
              {t('location')}
            </Typography>
            <div style={{ marginTop: theme.spacing(0.5) }}>
              <LocationChip
                onClick={handleLocationClick}
                mCellId={mCellId}
                inventoryCenterId={inventoryCenterId}
                mCellCollectionId={mCellCollectionId}
              />
            </div>
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} container>
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant={'caption'} color={'textSecondary'}>
              {t('title')}
            </Typography>
            <Typography>{title}</Typography>
          </div>
          <div>
            <EditIconButton
              onClick={() => handleDialogOpen('title')}
              tooltip={t('editTitle')}
            />
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} container>
          <div style={{ flexGrow: 1 }}>
            <Typography variant={'caption'} color={'textSecondary'}>
              {t('iconImage')}
            </Typography>
            {homeLink?.imageUrl && (
              <>
                <ImageCard src={homeLink?.imageUrl} height={60} width={60} />
              </>
            )}
          </div>
          <div>
            <EditIconButton
              onClick={handleIconImageAddClick}
              tooltip={t('iconImage')}
            />
            {homeLink?.imageUrl && (
              <IconButton onClick={handleIconImageDeleteClick} size='large'>
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} container>
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant={'caption'} color={'textSecondary'}>
              {t(homeLinkOptionToStringKey(homeLink.linkType))}
            </Typography>
            {homeLink?.linkUrl && (
              <Typography
                noWrap
                sx={{
                  wordWrap: 'break-word',
                  width: '30vw',
                }}
              >
                {homeLink?.linkUrl}
              </Typography>
            )}
            {homeLink?.fileUrl && (
              <Typography
                noWrap
                sx={{
                  wordWrap: 'break-word',
                  width: '30vw',
                }}
              >
                {homeLink?.filename}
              </Typography>
            )}
          </div>
          <div>
            {homeLink?.linkUrl && (
              <>
                <EditIconButton
                  onClick={() => handleDialogOpen('linkUrl')}
                  tooltip={homeLink?.linkType}
                />
              </>
            )}
            {homeLink?.fileUrl && (
              <>
                <EditIconButton
                  onClick={handleFileAddClick}
                  tooltip={homeLink?.linkType}
                />
              </>
            )}
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} container>
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant={'caption'} color={'textSecondary'}>
              {t('sortSequence')}
            </Typography>
            <Typography>{sortSequence}</Typography>
          </div>
          <div>
            <EditIconButton
              onClick={() => handleDialogOpen('sortSequence')}
              tooltip={t('sortSequence')}
            />
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} container>
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant={'caption'} color={'textSecondary'}>
              {t('group')}
            </Typography>
            <Typography>{groupName}</Typography>
          </div>
          <div>
            <EditIconButton
              onClick={() => handleDialogOpen('homeLinkGroupId')}
              tooltip={t('editGroup')}
            />
          </div>
        </Grid>
      </Grid>
      <TextInputDialog
        title={t('editTitle')}
        textLabel={t('title')}
        open={!!dialogOpenState.title}
        onConfirm={(v) => handleConfirm('title', v)}
        onCancel={() => handleDialogClose('title')}
        defaultText={homeLink?.title ? homeLink.title : ``}
        confirmDisabled={loadingHomeLinks}
      />
      <FileUploadDialog
        title={t('iconImageUpload')}
        initialFiles={[]}
        open={isIconImageAdd}
        onConfirm={handleIconImageAddConfirm}
        single={true}
        onCancel={handleIconImageAddClose}
        maxFileSizeBytes={HOME_LINK_IMAGE_MAX_FILE_SIZE_BYTES}
        accept={'image/png, image/jpeg, image/svg'}
      />
      <EnhancedSelectDialog
        title={t('editGroup')}
        open={!!dialogOpenState.homeLinkGroupId}
        data={homeLinkGroupOptions}
        initialValue={homeLinkGroupId || ''}
        onCancel={() => handleDialogClose('homeLinkGroupId')}
        onConfirm={(v) => handleConfirm('homeLinkGroupId', v)}
      />
      <ManufacturingLocationSelectionDialog
        initialManufacturingLocation={{
          mCellId: mCellId,
          inventoryCenterId: inventoryCenterId,
          mCellCollectionId: mCellCollectionId,
        }}
        open={isManufacturingLocationDialogOpen}
        onConfirm={handleConfirmManufacturingLocation}
        onCancel={handleLocationDialogCancel}
      />
      <TextInputDialog
        title={t('editLinkUrl')}
        textLabel={t('linkUrl')}
        open={!!dialogOpenState.linkUrl}
        onConfirm={(v) => handleConfirm('linkUrl', v)}
        onCancel={() => handleDialogClose('linkUrl')}
        defaultText={homeLink?.linkUrl ? homeLink.linkUrl : ``}
        confirmDisabled={loadingHomeLinks}
      />
      <TextInputDialog
        title={t('editSortSequence')}
        textLabel={t('sortSequence')}
        defaultText={
          homeLink?.sortSequence ? homeLink.sortSequence.toString() : '0'
        }
        open={!!dialogOpenState.sortSequence}
        onConfirm={(v) => handleConfirm('sortSequence', v)}
        onCancel={() => handleDialogClose('sortSequence')}
        allowEmpty
        type={'number'}
        inputProps={inputProps}
      />
      <FileUploadDialog
        title={t('fileUpload')}
        initialFiles={[]}
        open={isFileAdd}
        onConfirm={handleFileAddConfirm}
        onCancel={handleFileAddClose}
        single={true}
        maxFileSizeBytes={HOME_LINK_MAX_FILE_SIZE_BYTES}
      />
      <ConfirmDialog
        title={t('deleteIconImage')}
        message={t('deleteIconImage?', {
          messageImageName: homeLink?.imageName,
        })}
        open={isDeleteIconImageOpen}
        onConfirm={handleIconImageDeleteConfirm}
        onCancel={handleIconImageDeleteClose}
        confirmDisabled={loadingHomeLinks}
      />
      <ConfirmDialog
        title={t('deleteFile')}
        message={t('deleteFile?', { messageFilename: homeLink?.filename })}
        open={isDeleteFileOpen}
        onConfirm={handleFileDeleteConfirm}
        onCancel={handleFileDeleteClose}
        confirmDisabled={loadingHomeLinks}
      />
      <ConfirmDialog
        title={t('deleteHomeLink')}
        message={t('deleteHomeLink?', { messageTitle: homeLink?.title })}
        open={isDeleteDialogOpen}
        onConfirm={handleDeleteHomeLinkConfirm}
        onCancel={handleDeleteDialogClose}
        confirmDisabled={loadingHomeLinks}
      />
    </Page>
  );
};

type DialogState = Partial<Record<keyof HomeLinkInfo, boolean>>;

export default HomeLinkConfigDetailEdit;
